import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAppContext } from '@/composables/useAppContext';
import { useIssuesGrid } from '@/composables/useIssuesGrid';
import { useIssueCategoryChart } from '@/charts/configs/useIssueCategoryChart';
import { useIssueBurndownChart } from '@/charts/configs/useIssueBurndownChart';
import { useIssueTotals } from '@/composables/useIssueTotals';
import { useIssueStatusBreakdown } from '@/composables/useIssueStatusBreakdown';

export default () => {
  const store = useStore();
  const app = useAppContext();
  const dataLoaded = app.dataLoaded;
  const filterContext = app.filterIssuesContext;
  const filterStatusHistoryContext = app.filterStatusHistoryContext;
  const chartFontColor = app.chartFontColor;
  const conn = app.conn;

  const categoryChart = useIssueCategoryChart({ chartName: 'categoryStackedBarChart', filterContext, conn });
  // The burn-up (package BaseTimeSeriesChart). It reloads on EITHER context's ticks: its SQL reads both
  // context tables (Daniel, 2026-10-07 — the old SFC watched the status-history context only, so an
  // issues-only filter showed on its next reload); one stable array, so a re-render of this page does
  // not hand the chart a new one. The PNG download is the web app's only, as before.
  const burndownChart = useIssueBurndownChart({ conn });
  const burndownContexts = computed(() => [filterContext.value, filterStatusHistoryContext.value]);
  const showChartDownload = computed(() => app.buildMode.value === 'webapp');
  // The headline cards: the package TotalAndAssignedCards' spark row, fed by useIssueTotals (its SQL,
  // the weekly sparklines and the two "assigned to…" switch filters on both contexts).
  const {
    totalItems, totalAssignedToYourTeams, totalAssignedToYou, sparklines,
    filterByMyTeams, filterByMe, handleFilterChange,
  } = useIssueTotals({
    conn,
    filterContext,
    statusHistoryContext: filterStatusHistoryContext,
    userId: app.userId,
    dataLoaded,
  });
  // The Issue Status Breakdown card (the package StatusBreakdownCard since 2026-10-12): its query and
  // reloads (the data load, the issues context's ticks) are useIssueStatusBreakdown's.
  const { items: statusBreakdown, loading: statusBreakdownLoading } = useIssueStatusBreakdown({
    conn,
    filterContext,
    dataLoaded,
  });
  // The issues grid (the package IssuesGrid since 2026-10-12): its data, report and link opener are
  // useIssuesGrid's — one bag of props
  const issuesGrid = useIssuesGrid({
    conn,
    filterContext,
    dataLoaded,
    settings: app.settings,
    buildMode: app.buildMode,
    logoUrl: app.logoUrl,
    duckdb: app.duckdb,
  });

  onMounted(async () => {
    await store.commit('resetFilters');
  });

  return {
    dataLoaded,
    totalItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    sparklines,
    filterByMyTeams,
    filterByMe,
    handleFilterChange,
    statusBreakdown,
    statusBreakdownLoading,
    showChartDownload,
    burndownContexts,
    burndownChart,
    categoryChart,
    filterContext,
    chartFontColor,
    issuesGrid
  };
};
